import { createAction, createReducer } from "@reduxjs/toolkit";

import type { Gender, PayerId, PaymentMethodId, PaymentOptionId } from "~/common/constants";
import type { CourseId } from "~/common/courses";
import type { RootState } from "~/store";
import type { TPatientAudioNote } from "~/types/patient";

export type TInstructions = {
  additional_service_tags?: ("PRESCRIPTION_TRANSCRIBING" | "PRESCRIPTION_WITH_EXPERT" | "FAST_3D_PLAN" | "FAST_CAPS_PRINTING" | "LEAD_TECHNICIAN_3D_PLAN")[];
  doctor_id: number;
  payer_id: PayerId;
  payment_method_id: PaymentMethodId | null;
  clinic_id: number;
  course_id?: CourseId;
  /**
   * For additional aligners course only
   */
  patient_id?: number;
  payment_option_id?: PaymentOptionId | null;
  /**
   * Is `true` if doctor checked the deep cbct checkbox.
   */
  deep_cbct?: boolean | null;
  sber_credit?: boolean;
  condition?: string | null;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  middle_name?: string | null;
  bdate?: string | null;
  payer_last_name?: string | null;
  payer_first_name?: string | null;
  payer_patronymic?: string | null;
  sex?: Gender | null;
  test_plastic?: boolean;
  anthropometric_diagnostic?: boolean;
  promo_code?: string;
  // Draft/edit shape of the voice-guided prescription audio note. Same type as
  // the server response (`TPatientAudioNote`), just built incrementally.
  audio_note?: Partial<TPatientAudioNote> | null;
};

const initialState: Readonly<Partial<TInstructions>> = {};

export const createInstructionsSuccess = createAction("CREATE_PATIENT_INSTRUCTION");
export const addInstructions = createAction<typeof initialState>("ADD_PATIENT_INSTRUCTION");
export const removeInstructions = createAction<typeof initialState>("REMOVE_PATIENT_INSTRUCTION");

export const selectInstructions = (state: RootState): typeof initialState => state.instructions;

export const instructions = createReducer(initialState, (builder) =>
  builder
    .addCase(addInstructions, (state, action) => ({ ...state, ...action.payload }))
    .addCase(removeInstructions, (state, action) => {
      for (const key in action.payload) {
        delete state[key as keyof TInstructions];
      }
      return state;
    })
    .addCase(createInstructionsSuccess, () => ({}))
    .addCase("ERASE_INSTRUCTIONS", () => ({})),
);
